'use client';

// Device-scoped "seen" record for market lifecycle modals
// (spec 112-market-lifecycle-modals, FR-005). Each graduation / resolution
// modal is shown at most once per `wallet:market:stage` — the key is built by
// `seenKey()` in `@/lib/market/lifecycle-modal-eligibility`. Persistence is
// purely client-side via localStorage.

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/browser/safe-local-storage';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

interface MarketLifecycleSeenState {
  /** Sparse map of `${wallet}:${market}:${stage}` → true. Only seen entries stored. */
  seen: Record<string, true>;
  isSeen: (key: string) => boolean;
  markSeen: (key: string) => void;
}

export const useMarketLifecycleSeenStore: UseBoundStore<StoreApi<MarketLifecycleSeenState>> =
  createTrackedStore<MarketLifecycleSeenState>('useMarketLifecycleSeenStore')(
    persist(
      (set, get) => ({
        seen: {},
        isSeen: (key: string): boolean => get().seen[key] === true,
        markSeen: (key: string): void => {
          if (get().seen[key] === true) return;
          set(
            (state: MarketLifecycleSeenState): Pick<MarketLifecycleSeenState, 'seen'> => ({
              seen: { ...state.seen, [key]: true },
            }),
          );
        },
      }),
      {
        name: 'morfi-market-lifecycle-seen',
        partialize: (state: MarketLifecycleSeenState): Pick<MarketLifecycleSeenState, 'seen'> => ({
          seen: state.seen,
        }),
        storage: createJSONStorage(() => safeLocalStorage),
      },
    ),
  );
